import { useState, useCallback, useEffect } from "react";
import DataTable from '../DataTable/DataTable';
import { NotificationManager } from "react-notifications";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { convertDate } from "../AcitivityModal/assistingFunctions";
import resumeReport from "../../apis/ResumeReport/resume-report";
import { DateTime } from "luxon";
import "./requisitionReports.css";
import config from "../../Config.json";
const stylesSX = {
  odd: {
    background: '#F6F6F6 !important',
  },
  even: {
    background: '#ffffff !important',
  },
  backdrop: (theme) => ({
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  }),
  waitingText: {
    marginLeft: '5px',
  },
};


const ResumeReport = (props) => {
    const {
        userType,
        userIdType,
        userRecruitmentpartnerId,
        startSelectedDate,
        endSelectedDate,
        filter,
        exports,
        setTriggerResume
    } = props;

    const [takeNumberOfResult, setTakeNumberOfResult] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);
    const [dataResult, setDataResult] = useState([]);
    const [totalResult, setTotalResult] = useState(0);
    const [isSearching, setIsSearching] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [prevStartDate, setPrevStartDate] = useState(null);
    const [prevEndDate, setPrevEndDate] = useState(null);
    const [prevFilter, setPrevFilter] = useState({});
    // Define columns for the DataTable
    const columns = [
        {
            accessorKey: "candidateName",
            header: "Candidate Name",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
        },
        {
            accessorKey: "candidateEmailId",
            header: "Candidate Email",
            width: "300px",
            enableSorting: true,
            enableFiltering: false,
        },
        {
            accessorKey: "candidatePhoneNumber",
            header: "Candidate Phone Number",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ getValue }) => {
                const value = getValue();
                return value || "N/A";
            },
        },
        {
            accessorKey: "uploadedBy",
            header: "Recruiter Name",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
        },
        {
            accessorKey: "teamName",
            header: "Team Name",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
        },
        {
            accessorKey: "divisionName",
            header: "Division Name",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ getValue }) => {
                const value = getValue();
                return value || "N/A";
            },
        },
        {
            accessorKey: "dateOfUpload",
            header: "Date",
            width: "180px",
            enableSorting: true,
            enableFiltering: false,
            cell: ({ getValue }) => {
                const rawDate = getValue();
                if (!rawDate) return "";
                const date = new Date(rawDate);
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const year = date.getFullYear();
                return `${month}/${day}/${year}`;
            },
        },
    ];

    // Search function without debounce
    const performSearch = useCallback(async (
        userRecruitmentpartnerId,
        pageNumber,
        takeNumberOfResult,
        startSelectedDate,
        endSelectedDate,
        userId,
        additionalFilter
    ) => {
        try {
            setIsSearching(true);
            setDataResult([]);

            const filters = {
                divisions: additionalFilter?.divisionId || [],
                teams: additionalFilter?.teamId || [],
                candEmail: (additionalFilter?.email?.length ? [additionalFilter?.email] : []),
            };
            const payload = {
                orgId: userRecruitmentpartnerId,
                page: pageNumber,
                size: takeNumberOfResult,
                startDate: convertDate(startSelectedDate),
                endDate: convertDate(endSelectedDate),
                isExport: false,
                additionalFilter: filters,
            };
            const res = await resumeReport(payload);

            if (res?.total > 0) {
                const data = res.data.map(d => ({ ...d, billRate: d?.billRate?.maximum }));
                setDataResult(data);
                setTotalResult(res.total);
            } else {
                setDataResult([]);
                setTotalResult(0);
            }
            setIsSearching(false);
        } catch (error) {
            console.error("Error fetching resume report:", error);
            setDataResult([]);
            setTotalResult(0);
            setIsSearching(false);
            NotificationManager.error("Failed to load data", "Error", 10000);
        }
    }, []);


    // Handle page change
    const handlePageChange = (newPage, newPageSize) => {
        setPageNumber(newPage);
        if (newPageSize && newPageSize !== takeNumberOfResult) {
            setTakeNumberOfResult(newPageSize);
        }
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize) => {
        setTakeNumberOfResult(newPageSize);
        setPageNumber(1);
    };

    

    // Reset page if filter changes
    useEffect(() => {
        const datesChanged = prevStartDate !== startSelectedDate || prevEndDate !== endSelectedDate;
        const filtersChanged = JSON.stringify(prevFilter) !== JSON.stringify(filter);

        if (datesChanged || filtersChanged) {
            setPageNumber(1);
            setPrevStartDate(startSelectedDate);
            setPrevEndDate(endSelectedDate);
            setPrevFilter({ ...filter });
        }
    }, [startSelectedDate, endSelectedDate, filter, prevStartDate, prevEndDate, prevFilter]);

    useEffect(() => {
        const start = convertDate(startSelectedDate);
        const end = convertDate(endSelectedDate);
        
        if (!start || !end || start > end) {
            setDataResult([]);
            setTotalResult(0);
            NotificationManager.error("Please select a valid date range", "Error", 10000);
        } else {
            // When any dependency changes, schedule a search.
            setIsSearching(true);
            setDataResult([]);

            const timeoutId = setTimeout(() => {
                performSearch(
                    userRecruitmentpartnerId,
                    pageNumber,
                    takeNumberOfResult,
                    startSelectedDate,
                    endSelectedDate,
                    userIdType,
                    filter
                );
            }, 1000); // 1000ms debounce delay

            // Cleanup cancels the pending search if dependencies change quickly.
            return () => clearTimeout(timeoutId);
        }
    }, [pageNumber, takeNumberOfResult, startSelectedDate, endSelectedDate, filter, userRecruitmentpartnerId, userIdType, performSearch]);

    const callApiToGenerateExcel = useCallback(async () => {
        setIsExporting(true);
        setTriggerResume(false);
        const maxSize = config.MAX_SIZE ;
        const end = Math.ceil(totalResult / maxSize);
        let pageStart = 1;
        const exportArr = [];
        try {
            for (let start = 0; start < end; start++) {
                const payload = {
                    orgId: userRecruitmentpartnerId,
                    page: pageStart,
                    size: maxSize,
                    startDate: convertDate(startSelectedDate),
                    endDate: convertDate(endSelectedDate),
                    isExport: true,
                    userId: userIdType,
                    role: userType,
                    additionalFilter: {
                        divisions: filter?.divisionId || [],
                        teams: filter?.teamId || [],
                        candEmail: (filter?.email?.length ? [filter?.email] : []),
                    }
                };
                const res = await resumeReport(payload);
                if (res?.total > 0) {
                    res.data.forEach(({
                        candidateName,
                        candidateEmailId,
                        candidatePhoneNumber,
                        uploadedBy,
                        teamName,
                        dateOfUpload,
                        divisionName,
                        ...rest }) => {

                    exportArr.push({ 
                        "Candidate Name": candidateName,
                        "Candidate Email": candidateEmailId,
                        "Candidate PhoneNumber": candidatePhoneNumber,
                        "Recruiter Name": uploadedBy,
                        "Team Name": teamName,
                        "Date of upload": DateTime.fromISO(dateOfUpload)
                                    .toFormat("MM/dd/yyyy")
                                    .toString(),
                        "DivisionName": divisionName, 
                        ...rest 
                    });
                });
                }
                pageStart = pageStart + maxSize;
            }
            if (exportArr.length === 0) {
                NotificationManager.error("No data available", "Error", 10000);
                setIsExporting(false);
            } else {
                // Lazy load XLSX only when needed for export
                const { utils, writeFile } = await import("xlsx");
                const ws = utils.json_to_sheet(exportArr);
                const wb = utils.book_new();
                utils.book_append_sheet(wb, ws, "Resume Report");
                writeFile(wb, "Resume_report.xlsx");
                setIsExporting(false);
            }

        } catch (error) {
            console.error("Error exporting:", error);
            NotificationManager.error("Failed to export", "Error", 10000);
            setIsExporting(false);
        }
    }, [totalResult, userRecruitmentpartnerId, startSelectedDate, endSelectedDate, userIdType, userType, filter, setTriggerResume]);

    useEffect(() => {
        if (exports) {
            callApiToGenerateExcel();
        }
    }, [exports, callApiToGenerateExcel]);

    return (
        <>
            <Backdrop sx={stylesSX.backdrop} open={isExporting}>
                <CircularProgress color="inherit" />
                <Typography sx={stylesSX.waitingText}>Please wait...</Typography>
            </Backdrop>

            <DataTable
                data={dataResult}
                columns={columns}
                isLoading={isSearching}
                totalRecords={totalResult}
                currentPage={pageNumber}
                pageSize={takeNumberOfResult}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableFiltering={false} 
                enablePagination={true}
                enableSorting={false} 
                pageSizeOptions={[10, 20, 50, 100]}
                isServerSide={true}
                className="kendo_reports"
                noDataText="No results found !"
            />
        </>
    );
};

export default ResumeReport;
